import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { uploadFile } from "@client/lib/uploads"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { ConfirmModal } from "./components/confirm-modal"
import { DetailPageSkeleton } from "./components/skeleton"
import { SplitActionButton } from "./components/split-action-button"

type BeneficiaryStatus = "nominated" | "verified" | "active" | "concluded"
type BeneficiaryCategory = "mobility" | "visual" | "hearing" | "limb_difference" | "other"
type BeneficiaryAgeGroup = "child" | "teen" | "university" | "adult"
type SupportItemType = "device" | "fees" | "transport" | "therapy" | "other"

function todayDate() {
  return new Date().toISOString().split("T")[0] ?? ""
}

function toIsoDateTime(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toISOString()
}

interface Beneficiary {
  id: string
  code: string
  fullName: string
  dateOfBirth: string
  gender: string
  ageGroup: BeneficiaryAgeGroup
  photoUrl: string | null
  category: BeneficiaryCategory
  condition: string
  onset: string | null
  aidsInUse: string | null
  clinicTherapist: string | null
  medicalNotes: string | null
  contactName: string | null
  contactPhone: string | null
  contactRelationship: string | null
  state: string
  lga: string | null
  about: string | null
  institution: string | null
  institutionRole: string | null
  accessibilityNeeds: string | null
  plannedSupportItems: string | null
  totalSupportValue: number
  sponsorId: string | null
  sponsorName: string | null
  sponsoredAt: string | null
  status: BeneficiaryStatus
  documents: {
    name: string
    key: string
    url: string
    purpose: string
    contentType: string
    sizeBytes: number
    uploadedAt: string
  }[]
  activity: { event: string; date: string; note?: string }[]
  createdAt: string
  updatedAt: string
}

interface Device {
  id: string
  beneficiaryId: string
  name: string
  serialNumber: string | null
  issuedAt: string
  condition: string
  maintenanceLog: { date: string; description: string }[]
  createdAt: string
  updatedAt: string
}

interface SupportItem {
  id: string
  beneficiaryId: string
  description: string
  type: SupportItemType
  cost: number
  date: string
  status: string
  createdAt: string
  updatedAt: string
}

const STATUS_COLORS: Record<BeneficiaryStatus, { bg: string; text: string }> = {
  nominated: { bg: "bg-orange-100", text: "text-orange-700" },
  verified: { bg: "bg-blue-100", text: "text-blue-700" },
  active: { bg: "bg-green-100", text: "text-green-700" },
  concluded: { bg: "bg-gray-100", text: "text-gray-700" },
}

const CATEGORY_LABELS: Record<BeneficiaryCategory, string> = {
  mobility: "Mobility",
  visual: "Visual",
  hearing: "Hearing",
  limb_difference: "Limb difference",
  other: "Other",
}

const AGE_GROUP_LABELS: Record<BeneficiaryAgeGroup, string> = {
  child: "Child",
  teen: "Teen",
  university: "University",
  adult: "Adult",
}

const SUPPORT_TYPE_LABELS: Record<SupportItemType, string> = {
  device: "Device",
  fees: "Fees",
  transport: "Transport",
  therapy: "Therapy",
  other: "Other",
}

type Tab = "profile" | "support" | "devices" | "documents" | "timeline"
const VALID_TABS: readonly Tab[] = ["profile", "support", "devices", "documents", "timeline"]

function getTabFromSearch(value: string | null): Tab {
  return VALID_TABS.includes(value as Tab) ? (value as Tab) : "profile"
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0 }).format(amount)
}

function getAboutNotes(about: string | null): { label: string | null; value: string }[] {
  if (!about) return []

  const duplicatedFields = new Set([
    "program",
    "disability category",
    "support most needed",
    "condition and needs",
    "contact name",
    "contact phone",
  ])

  const notes: { label: string | null; value: string }[] = []
  for (const line of about.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)) {
    const separatorIndex = line.indexOf(":")
    if (separatorIndex === -1) {
      notes.push({ label: null, value: line })
      continue
    }

    const rawLabel = line.slice(0, separatorIndex).trim()
    const value = line.slice(separatorIndex + 1).trim()
    if (!value || duplicatedFields.has(rawLabel.toLowerCase())) continue
    if (rawLabel.toLowerCase() === "anything else" && /^(nothing|none|n\/a)\b/i.test(value)) continue

    notes.push({ label: rawLabel.toLowerCase() === "anything else" ? "Additional notes" : rawLabel, value })
  }
  return notes
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

export function AbilityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canView = useHasPermission("ability")
  const canMutate = useHasPermission("ability_mutate")
  const [beneficiary, setBeneficiary] = useState<Beneficiary | null>(null)
  const [devices, setDevices] = useState<Device[]>([])
  const [supportItems, setSupportItems] = useState<SupportItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>(() => getTabFromSearch(searchParams.get("tab")))
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddActivityModal, setShowAddActivityModal] = useState(false)
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const [editingDevice, setEditingDevice] = useState<Device | null>(null)
  const [showAddSupportModal, setShowAddSupportModal] = useState(false)
  const [editingSupportItem, setEditingSupportItem] = useState<SupportItem | null>(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<BeneficiaryStatus>("nominated")
  const [saving, setSaving] = useState(false)
  const [documentUploading, setDocumentUploading] = useState(false)
  const documentInputRef = useRef<HTMLInputElement>(null)

  const [activityForm, setActivityForm] = useState({ event: "", note: "" })
  const [deviceForm, setDeviceForm] = useState({ name: "", serialNumber: "", issuedAt: todayDate(), condition: "good" })
  const [supportForm, setSupportForm] = useState({ description: "", type: "device" as SupportItemType, cost: "", date: todayDate(), status: "pending" })

  useEffect(() => {
    setActiveTab(getTabFromSearch(searchParams.get("tab")))
  }, [searchParams])

  const handleTabChange = (tab: Tab) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set("tab", tab)
    setSearchParams(nextParams, { replace: true })
  }

  const fetchBeneficiary = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.ability[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setBeneficiary(body.data)
      }
    } catch {
      setError("Failed to load beneficiary")
    } finally {
      setLoading(false)
    }
  }

  const fetchDevices = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.ability[":id"].devices.$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setDevices(body.data)
      }
    } catch {
      // Devices are optional
    }
  }

  const fetchSupportItems = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.ability[":id"].support.$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setSupportItems(body.data)
      }
    } catch {
      // Support items are optional
    }
  }

  const reloadPageData = async () => {
    await Promise.all([fetchBeneficiary(), fetchDevices(), fetchSupportItems()])
  }

  useEffect(() => {
    if (canView && id) {
      void fetchBeneficiary()
      void fetchDevices()
      void fetchSupportItems()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const handleStatusChange = async () => {
    if (!beneficiary) return
    setSaving(true)
    try {
      const res = await client.api.admin.ability[":id"].$patch({
        param: { id: beneficiary.id },
        json: { status: pendingStatus },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setBeneficiary(body.data)
        toast.success("Status updated")
        setShowStatusModal(false)
        await reloadPageData()
      }
    } catch {
      toast.error("Failed to update status")
    } finally {
      setSaving(false)
    }
  }

  const handleAddActivity = async () => {
    if (!beneficiary || !activityForm.event.trim()) return
    setSaving(true)
    try {
      const res = await client.api.admin.ability[":id"].activity.$post({
        param: { id: beneficiary.id },
        json: { event: activityForm.event.trim(), note: activityForm.note.trim() || undefined },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setBeneficiary(body.data)
        toast.success("Activity logged")
        setShowAddActivityModal(false)
        setActivityForm({ event: "", note: "" })
        await reloadPageData()
      }
    } catch {
      toast.error("Failed to log activity")
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDevice = async () => {
    if (!beneficiary || !deviceForm.name.trim()) return
    setSaving(true)
    try {
      const deviceData = {
        name: deviceForm.name.trim(),
        issuedAt: toIsoDateTime(deviceForm.issuedAt),
        condition: deviceForm.condition as "new" | "good" | "fair" | "needs_repair",
      }
      const res = editingDevice
        ? await client.api.admin.ability[":id"].devices[":deviceId"].$patch({
            param: { id: beneficiary.id, deviceId: editingDevice.id },
            json: { ...deviceData, serialNumber: deviceForm.serialNumber.trim() || null },
          })
        : await client.api.admin.ability[":id"].devices.$post({
            param: { id: beneficiary.id },
            json: { ...deviceData, serialNumber: deviceForm.serialNumber.trim() || undefined },
          })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setDevices((current) => editingDevice
          ? current.map((device) => device.id === editingDevice.id ? body.data : device)
          : [...current, body.data])
        toast.success(editingDevice ? "Device updated" : "Device added")
        setShowAddDeviceModal(false)
        setEditingDevice(null)
        setDeviceForm({ name: "", serialNumber: "", issuedAt: todayDate(), condition: "good" })
        await reloadPageData()
      }
    } catch {
      toast.error(editingDevice ? "Failed to update device" : "Failed to add device")
    } finally {
      setSaving(false)
    }
  }

  const handleAddSupportItem = async () => {
    if (!beneficiary || !supportForm.description.trim()) return
    setSaving(true)
    try {
      const supportData = {
        description: supportForm.description.trim(),
        type: supportForm.type,
        cost: Number.parseInt(supportForm.cost, 10) || 0,
        date: toIsoDateTime(supportForm.date),
        status: supportForm.status as "pending" | "approved" | "completed",
      }
      const res = editingSupportItem
        ? await client.api.admin.ability[":id"].support[":itemId"].$patch({
            param: { id: beneficiary.id, itemId: editingSupportItem.id },
            json: supportData,
          })
        : await client.api.admin.ability[":id"].support.$post({
            param: { id: beneficiary.id },
            json: supportData,
          })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setSupportItems((current) => editingSupportItem
          ? current.map((item) => item.id === editingSupportItem.id ? body.data : item)
          : [...current, body.data])
        toast.success(editingSupportItem ? "Support item updated" : "Support item added")
        setShowAddSupportModal(false)
        setEditingSupportItem(null)
        setSupportForm({ description: "", type: "device", cost: "", date: todayDate(), status: "pending" })
        await reloadPageData()
      }
    } catch {
      toast.error("Failed to add support item")
    } finally {
      setSaving(false)
    }
  }

  const openStatusModal = () => {
    if (!beneficiary) return
    setPendingStatus(beneficiary.status)
    setShowStatusModal(true)
  }

  const openAddSupportModal = () => {
    setEditingSupportItem(null)
    setSupportForm({ description: "", type: "device", cost: "", date: todayDate(), status: "pending" })
    setShowAddSupportModal(true)
  }

  const openAddDeviceModal = () => {
    setEditingDevice(null)
    setDeviceForm({ name: "", serialNumber: "", issuedAt: todayDate(), condition: "good" })
    setShowAddDeviceModal(true)
  }

  const openEditDeviceModal = (device: Device) => {
    setEditingDevice(device)
    setDeviceForm({
      name: device.name,
      serialNumber: device.serialNumber ?? "",
      issuedAt: device.issuedAt.split("T")[0] ?? device.issuedAt,
      condition: device.condition,
    })
    setShowAddDeviceModal(true)
  }

  const openEditSupportModal = (item: SupportItem) => {
    setEditingSupportItem(item)
    setSupportForm({
      description: item.description,
      type: item.type,
      cost: String(item.cost),
      date: item.date.split("T")[0] ?? item.date,
      status: item.status,
    })
    setShowAddSupportModal(true)
  }

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ""
    if (!beneficiary || files.length === 0 || documentUploading) return

    setDocumentUploading(true)
    try {
      const results = await Promise.allSettled(files.map((file) => uploadFile(file, "ability-document")))
      const uploaded = results.flatMap((result) => result.status === "fulfilled" ? [result.value] : [])
      const failed = results.filter((result) => result.status === "rejected").length

      if (uploaded.length === 0) {
        toast.error("Failed to upload document")
        return
      }

      const res = await client.api.admin.ability[":id"].$patch({
        param: { id: beneficiary.id },
        json: {
          documents: [
            ...beneficiary.documents.map(({ url: _url, ...document }) => ({
              ...document,
              purpose: document.purpose as "ability-document" | "ability-medical",
            })),
            ...uploaded.map((document) => ({ ...document, purpose: "ability-document" as const })),
          ],
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }

      await reloadPageData()
      toast.success(`${uploaded.length} document${uploaded.length === 1 ? "" : "s"} uploaded${failed ? `, ${failed} failed` : ""}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload document")
    } finally {
      setDocumentUploading(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Beneficiary Details</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view ability beneficiaries.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !beneficiary) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate("/admin/ability")} className="flex items-center gap-2 text-sm text-ink/60 hover:text-ink">
          <BackIcon className="h-4 w-4" />
          Back to Ability Program
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "Beneficiary not found"}</p>
        </div>
      </div>
    )
  }

  const aboutNotes = getAboutNotes(beneficiary.about)
  const tabs: { key: Tab; label: string }[] = [
    { key: "profile", label: "Profile" },
    { key: "support", label: "Support" },
    { key: "devices", label: "Devices" },
    { key: "documents", label: "Documents" },
    { key: "timeline", label: "Timeline" },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <button onClick={() => navigate("/admin/ability")} className="mt-2 text-ink/60 hover:text-ink">
            <BackIcon className="h-5 w-5" />
          </button>
          <div className="flex items-start gap-4">
            {beneficiary.photoUrl ? (
              <img src={beneficiary.photoUrl} alt={beneficiary.fullName} className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-lg font-bold text-white">
                {beneficiary.fullName.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </span>
            )}
            <div>
              <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-3xl">{beneficiary.fullName}</h1>
              <p className="text-sm text-ink/60">{beneficiary.code} · {CATEGORY_LABELS[beneficiary.category]}</p>
              <span className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[beneficiary.status].bg} ${STATUS_COLORS[beneficiary.status].text}`}>
                {beneficiary.status}
              </span>
            </div>
          </div>
        </div>
        {canMutate && (
          <>
            <input
              ref={documentInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              multiple
              className="hidden"
              onChange={handleDocumentChange}
            />
            <SplitActionButton
              label="Add support item"
              icon={<PlusIcon className="h-4 w-4" />}
              onClick={openAddSupportModal}
              disabled={documentUploading}
              items={[
                { label: "Add device", onClick: openAddDeviceModal },
                { label: "Upload document", onClick: () => documentInputRef.current?.click() },
                { label: "Change status", onClick: openStatusModal },
              ]}
            />
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-gold-500/30">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "border-forest-950 text-forest-950"
                : "border-transparent text-ink/60 hover:text-ink"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "profile" && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Identity */}
          <section className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-forest-950">Identity</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">Full name</dt>
                <dd className="text-sm font-medium text-ink">{beneficiary.fullName}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">Date of birth</dt>
                <dd className="text-sm font-medium text-ink">{formatDate(beneficiary.dateOfBirth)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">Gender</dt>
                <dd className="text-sm font-medium text-ink capitalize">{beneficiary.gender}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">Age group</dt>
                <dd className="text-sm font-medium text-ink">{AGE_GROUP_LABELS[beneficiary.ageGroup]}</dd>
              </div>
            </dl>
          </section>

          {/* Disability Profile */}
          <section className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-forest-950">Disability Profile</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">Category</dt>
                <dd className="text-sm font-medium text-ink">{CATEGORY_LABELS[beneficiary.category]}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">Condition</dt>
                <dd className="text-sm font-medium text-ink">{beneficiary.condition}</dd>
              </div>
              {beneficiary.onset && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Onset</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.onset}</dd>
                </div>
              )}
              {beneficiary.aidsInUse && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Aids in use</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.aidsInUse}</dd>
                </div>
              )}
              {beneficiary.clinicTherapist && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Clinic/Therapist</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.clinicTherapist}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Contact & Location */}
          <section className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-forest-950">Contact & Location</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-ink/60">State</dt>
                <dd className="text-sm font-medium text-ink">{beneficiary.state}</dd>
              </div>
              {beneficiary.lga && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">LGA</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.lga}</dd>
                </div>
              )}
              {beneficiary.contactName && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Contact name</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.contactName}</dd>
                </div>
              )}
              {beneficiary.contactPhone && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Contact phone</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.contactPhone}</dd>
                </div>
              )}
              {beneficiary.contactRelationship && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Relationship</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.contactRelationship}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Education/Employment */}
          <section className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-forest-950">Education / Employment</h2>
            <dl className="space-y-3">
              {beneficiary.institution && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Institution</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.institution}</dd>
                </div>
              )}
              {beneficiary.institutionRole && (
                <div className="flex justify-between">
                  <dt className="text-sm text-ink/60">Role</dt>
                  <dd className="text-sm font-medium text-ink">{beneficiary.institutionRole}</dd>
                </div>
              )}
              {beneficiary.accessibilityNeeds && (
                <div>
                  <dt className="text-sm text-ink/60">Accessibility needs</dt>
                  <dd className="mt-1 text-sm text-ink">{beneficiary.accessibilityNeeds}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* About */}
          {aboutNotes.length > 0 && (
            <section className="rounded-[10px] border border-gold-500 bg-white p-5 lg:col-span-2">
              <h2 className="mb-4 text-lg font-semibold text-forest-950">About</h2>
              <dl className="space-y-3">
                {aboutNotes.map((note, index) => (
                  <div key={`${note.label ?? "note"}-${index}`}>
                    {note.label ? <dt className="text-sm text-ink/60">{note.label}</dt> : null}
                    <dd className={`text-sm text-ink ${note.label ? "mt-1" : ""}`}>{note.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      )}

      {activeTab === "support" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-forest-950">Support Items</h2>
              <p className="text-sm text-ink/60">Total support value: {formatCurrency(beneficiary.totalSupportValue)}</p>
            </div>
            {canMutate && (
              <button
                onClick={openAddSupportModal}
                className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900"
              >
                <PlusIcon className="h-4 w-4" />
                Add item
              </button>
            )}
          </div>

          {supportItems.length === 0 ? (
            <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
              <p className="text-ink/50">No support items recorded yet</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gold-500/50">
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Description</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Type</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Cost</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Date</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Status</th>
                    <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-ink/60">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gold-500/30">
                  {supportItems.map((item) => (
                    <tr key={item.id}>
                      <td className="px-5 py-4 text-sm text-ink">{item.description}</td>
                      <td className="px-5 py-4 text-sm text-ink">{SUPPORT_TYPE_LABELS[item.type]}</td>
                      <td className="px-5 py-4 text-sm text-ink">{formatCurrency(item.cost)}</td>
                      <td className="px-5 py-4 text-sm text-ink">{formatDate(item.date)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                          item.status === "completed" ? "bg-green-100 text-green-700" :
                          item.status === "approved" ? "bg-blue-100 text-blue-700" :
                          "bg-orange-100 text-orange-700"
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {canMutate && (
                          <button
                            type="button"
                            onClick={() => openEditSupportModal(item)}
                            aria-label={`Edit ${item.description}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-forest-700 px-3 py-1.5 text-xs font-medium text-forest-700 hover:bg-cream-100"
                          >
                            <EditIcon className="h-3.5 w-3.5" />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "devices" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-forest-950">Devices Issued</h2>
            {canMutate && (
              <button
                onClick={openAddDeviceModal}
                className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900"
              >
                <PlusIcon className="h-4 w-4" />
                Add device
              </button>
            )}
          </div>

          {devices.length === 0 ? (
            <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
              <p className="text-ink/50">No devices issued yet</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {devices.map((device) => (
                <div key={device.id} className="rounded-[10px] border border-gold-500 bg-white p-5">
                  <h3 className="font-semibold text-forest-950">{device.name}</h3>
                  {device.serialNumber && <p className="text-sm text-ink/60">S/N: {device.serialNumber}</p>}
                  <div className="mt-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-ink/60">Issued</span>
                      <span className="text-ink">{formatDate(device.issuedAt)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ink/60">Condition</span>
                      <span className="text-ink capitalize">{device.condition.replace("_", " ")}</span>
                    </div>
                  </div>
                  {device.maintenanceLog.length > 0 && (
                    <div className="mt-3 border-t border-gold-500/30 pt-3">
                      <p className="text-xs font-medium text-ink/60">Maintenance log</p>
                      <ul className="mt-1 space-y-1">
                        {device.maintenanceLog.slice(-3).map((log, i) => (
                          <li key={i} className="text-xs text-ink">
                            {formatDate(log.date)}: {log.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {canMutate && (
                    <button
                      type="button"
                      onClick={() => openEditDeviceModal(device)}
                      aria-label={`Edit ${device.name}`}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-forest-700 px-3 py-1.5 text-xs font-medium text-forest-700 hover:bg-cream-100"
                    >
                      <EditIcon className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "documents" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-forest-950">Documents</h2>
            {canMutate && (
              <button
                type="button"
                onClick={() => documentInputRef.current?.click()}
                disabled={documentUploading}
                className="rounded-full border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700 hover:bg-cream-100 disabled:pointer-events-none disabled:opacity-50"
              >
                {documentUploading ? "Uploading..." : "Upload document"}
              </button>
            )}
          </div>

          {beneficiary.documents.length === 0 ? (
            <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
              <p className="text-ink/50">No documents uploaded yet</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {beneficiary.documents.map((doc) => (
                <a
                  key={doc.key}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-[10px] border border-gold-500 bg-white p-4 hover:bg-cream-50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded bg-forest-950/10">
                    <svg className="h-5 w-5 text-forest-950" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-medium text-ink">{doc.name}</p>
                    <p className="text-xs text-ink/50">{formatDate(doc.uploadedAt)}</p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "timeline" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-forest-950">Activity Timeline</h2>
            {canMutate && (
              <button
                onClick={() => setShowAddActivityModal(true)}
                className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900"
              >
                <PlusIcon className="h-4 w-4" />
                Log update
              </button>
            )}
          </div>

          {beneficiary.activity.length === 0 ? (
            <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
              <p className="text-ink/50">No activity recorded yet</p>
            </div>
          ) : (
            <div className="rounded-[10px] border border-gold-500 bg-white">
              <ul className="divide-y divide-gold-500/30">
                {[...beneficiary.activity].reverse().map((activity, index) => (
                  <li key={index} className="flex gap-4 p-5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-forest-950/10">
                      <span className="h-2 w-2 rounded-full bg-forest-950" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink">{activity.event}</p>
                      {activity.note && <p className="mt-1 text-sm text-ink/60">{activity.note}</p>}
                      <p className="mt-1 text-xs text-ink/40">{formatDate(activity.date)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Status Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Change Status">
        <div className="space-y-4">
          <Select
            label="Status"
            value={pendingStatus}
            onChange={(value) => setPendingStatus(value as BeneficiaryStatus)}
            options={["nominated", "verified", "active", "concluded"]}
          />
        </div>
        <ModalActions>
          <ModalButton variant="secondary" onClick={() => setShowStatusModal(false)}>Cancel</ModalButton>
          <ModalButton variant="primary" onClick={handleStatusChange} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </ModalButton>
        </ModalActions>
      </Modal>

      {/* Add Activity Modal */}
      <Modal isOpen={showAddActivityModal} onClose={() => setShowAddActivityModal(false)} title="Log Activity">
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Event</label>
            <input
              type="text"
              value={activityForm.event}
              onChange={(e) => setActivityForm({ ...activityForm, event: e.target.value })}
              placeholder="e.g., Device issued, Assessment completed"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Note (optional)</label>
            <textarea
              value={activityForm.note}
              onChange={(e) => setActivityForm({ ...activityForm, note: e.target.value })}
              rows={3}
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
        </div>
        <ModalActions>
          <ModalButton variant="secondary" onClick={() => setShowAddActivityModal(false)}>Cancel</ModalButton>
          <ModalButton variant="primary" onClick={handleAddActivity} disabled={saving || !activityForm.event.trim()}>
            {saving ? "Saving..." : "Log Activity"}
          </ModalButton>
        </ModalActions>
      </Modal>

      {/* Add/Edit Device Modal */}
      <Modal
        isOpen={showAddDeviceModal}
        onClose={() => {
          setShowAddDeviceModal(false)
          setEditingDevice(null)
        }}
        title={editingDevice ? "Edit Device" : "Add Device"}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Device name</label>
            <input
              type="text"
              value={deviceForm.name}
              onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
              placeholder="e.g., Hearing aid, Wheelchair"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Serial number (optional)</label>
            <input
              type="text"
              value={deviceForm.serialNumber}
              onChange={(e) => setDeviceForm({ ...deviceForm, serialNumber: e.target.value })}
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <DatePicker
            label="Issued date"
            value={deviceForm.issuedAt}
            onChange={(value) => setDeviceForm({ ...deviceForm, issuedAt: value })}
          />
          <Select
            label="Condition"
            value={deviceForm.condition}
            onChange={(value) => setDeviceForm({ ...deviceForm, condition: value })}
            options={["new", "good", "fair", "needs_repair"]}
          />
        </div>
        <ModalActions>
          <ModalButton variant="secondary" onClick={() => {
            setShowAddDeviceModal(false)
            setEditingDevice(null)
          }}>Cancel</ModalButton>
          <ModalButton variant="primary" onClick={handleSaveDevice} disabled={saving || !deviceForm.name.trim()}>
            {saving ? "Saving..." : editingDevice ? "Save Changes" : "Add Device"}
          </ModalButton>
        </ModalActions>
      </Modal>

      {/* Add/Edit Support Item Modal */}
      <Modal
        isOpen={showAddSupportModal}
        onClose={() => {
          setShowAddSupportModal(false)
          setEditingSupportItem(null)
        }}
        title={editingSupportItem ? "Edit Support Item" : "Add Support Item"}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Description</label>
            <input
              type="text"
              value={supportForm.description}
              onChange={(e) => setSupportForm({ ...supportForm, description: e.target.value })}
              placeholder="e.g., School fees, Transport allowance"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <Select
            label="Type"
            value={supportForm.type}
            onChange={(value) => setSupportForm({ ...supportForm, type: value as SupportItemType })}
            options={["device", "fees", "transport", "therapy", "other"]}
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Cost (NGN)</label>
            <input
              type="number"
              value={supportForm.cost}
              onChange={(e) => setSupportForm({ ...supportForm, cost: e.target.value })}
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <DatePicker
            label="Date"
            value={supportForm.date}
            onChange={(value) => setSupportForm({ ...supportForm, date: value })}
          />
          <Select
            label="Status"
            value={supportForm.status}
            onChange={(value) => setSupportForm({ ...supportForm, status: value })}
            options={["pending", "approved", "completed"]}
          />
        </div>
        <ModalActions>
          <ModalButton variant="secondary" onClick={() => {
            setShowAddSupportModal(false)
            setEditingSupportItem(null)
          }}>Cancel</ModalButton>
          <ModalButton variant="primary" onClick={handleAddSupportItem} disabled={saving || !supportForm.description.trim()}>
            {saving ? "Saving..." : editingSupportItem ? "Save Changes" : "Add Item"}
          </ModalButton>
        </ModalActions>
      </Modal>
    </div>
  )
}
